import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Country-aware trust pill.
 *
 * Replaces the IE-only `<PsraPill>`. Renders:
 *   - 🇮🇪 IE listing →  PSRA #XXXX ✓
 *   - 🇬🇧 UK listing →  Propertymark XXXX ✓
 *   - Anything else / private / scraped → nothing
 *
 * Single network call to `/api/properties/{id}/trust-v2`. Decides what
 * to show purely on the server-returned `regulator.value` — clients
 * never invent licence numbers.
 */
export const CountryTrustPill = ({ propertyId, compact = false }) => {
  const [trust, setTrust] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    fetch(`${BACKEND_URL}/api/properties/${propertyId}/trust-v2`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled) {
          setTrust(d);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  if (!loaded || !trust?.regulator?.value) return null;

  const reg = trust.regulator;
  const slug = trust.agency_slug;
  const inner = (
    <Badge
      variant="secondary"
      className={`bg-green-100 text-green-800 hover:bg-green-200 ${compact ? 'text-[10px] px-1.5 py-0' : ''}`}
      data-testid="country-trust-pill"
      data-country={trust.country_code}
    >
      <ShieldCheck className={`${compact ? 'h-2.5 w-2.5' : 'h-3 w-3'} mr-1`} />
      {reg.name} {reg.value}
    </Badge>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {slug ? <Link to={`/agency/${slug}`}>{inner}</Link> : inner}
        </TooltipTrigger>
        <TooltipContent side="top">
          <div className="text-xs max-w-[240px]">
            <p className="font-semibold mb-0.5">State-registered agent</p>
            <p className="text-gray-600">
              {trust.agency_name || 'This agency'} is registered with {reg.full_name}.{' '}
              {reg.licence_label}: <span className="font-mono">{reg.value}</span>.
            </p>
            {reg.verify_url && (
              <a
                href={reg.verify_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#7042cc] underline mt-1 inline-block"
              >
                Verify on {reg.name} register →
              </a>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CountryTrustPill;
